/**
 * Public entry of the annotation engine (contract: ./contract.ts).
 *
 *   useSheetAnnotations(file)      layers + objects of one sheet for the signed-in user, editing, sync
 *   pendingAnnotationOps(userId)   unsent annotation/layer changes of that user on this device
 *   flushAnnotationOps()           try to send everything now (logout); never rejects
 *   purgeSheetAnnotations(ids)     forget local layers/objects of sheets that are no longer visible
 *                                  (true once nothing of them is stored any more)
 *   purgeAnnotationsNotIn(uid, ids) the same for every stored sheet the manifest of `uid` does not list
 *   forgetPulledAnnotations()      the session was revoked: forget everything pulled from the server,
 *                                  keep the unsent changes (offline/wipe.ts wipeRevokedData)
 *   startAnnotationSync()          call once per signed-in session (after ensureSingleUser): installs
 *                                  the online/visibility/interval triggers and sends edits queued in
 *                                  an earlier session without waiting for a song to be opened.
 *                                  Idempotent and safe to call while nobody is signed in.
 *
 * Editing calls are accepted once the local database has been read (status leaves 'loading', or
 * stays 'loading' only when IndexedDB is unavailable and the first pull is still running); the UI
 * should gate edit mode on status === 'ready'.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { AnnotationKind, AnnotationObject, AnnotationOf, DataByKind } from '../../shared/annotations';
import type { SongFileDTO } from '../../shared/dto/library';
import { uuid } from '../../shared/ids';
import { ApiFailure } from '../api/client';
import { useMe } from '../auth/AuthProvider';
import { useT } from '../i18n';
import type { SheetAnnotations } from './contract';
import { engine } from './engine/engine';

const EMPTY_PAGES: ReadonlyMap<number, readonly AnnotationObject[]> = new Map<number, readonly AnnotationObject[]>();
const noopSubscribe = () => () => undefined;

/** Signed out (or between users): nothing to show, every edit is a no-op. */
const SIGNED_OUT: SheetAnnotations = {
  status: 'loading',
  layers: [],
  activeLayerId: null,
  setActiveLayer: () => undefined,
  objectsByPage: EMPTY_PAGES,
  connectors: [],
  get: () => undefined,
  isMine: () => false,
  newObject: <K extends AnnotationKind>(page: number, kind: K, data: DataByKind[K]) =>
    ({ id: uuid(), layerId: '', page, kind, data, hlcMs: 0, deviceId: '', deletedAt: null }) as unknown as AnnotationOf<K>,
  apply: () => undefined,
  undo: () => undefined,
  redo: () => undefined,
  canUndo: false,
  canRedo: false,
  createLayer: () => '',
  renameLayer: () => undefined,
  deleteLayer: () => undefined,
  setLayerVisible: () => undefined,
  shareLayer: () => Promise.reject(new ApiFailure(401, 'unauthorized')),
  copyToMine: () => '',
  confirmLayerRevision: () => undefined,
  setHideStaleLayers: () => undefined,
  sync: { status: 'idle', pending: 0, lastSyncAt: null, flush: () => Promise.resolve() },
  hasStaleLayers: false,
  hideStaleLayers: false,
};
const signedOutSnapshot = () => SIGNED_OUT;

export function useSheetAnnotations(file: SongFileDTO): SheetAnnotations {
  const me = useMe();
  const t = useT();
  const genId = useSyncExternalStore(engine.subscribeGen, engine.getGenId, engine.getGenId);
  const meId = me?.id ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by user, sheet and generation only
  const store = useMemo(() => (me ? engine.storeFor(me, file) : null), [meId, file.id, genId]);

  useEffect(() => {
    if (!store) return;
    store.retain();
    return () => store.release();
  }, [store]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- only revision/page count matter
  useEffect(() => store?.setFile(file), [store, file.rev, file.pageCount, file.pages?.length]);
  useEffect(() => {
    if (store && me) store.setMe(me);
  }, [store, me]);
  const defaultName = t('sync.default_layer');
  useEffect(() => store?.setDefaultLayerName(defaultName), [store, defaultName]);

  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    store ? store.getSnapshot : signedOutSnapshot,
    store ? store.getSnapshot : signedOutSnapshot,
  );
}

// Device-level helpers (no React): see ./local.ts.
export {
  flushAnnotationOps,
  forgetPulledAnnotations,
  pendingAnnotationOps,
  purgeAnnotationsNotIn,
  purgeSheetAnnotations,
  startAnnotationSync,
} from './local';
